import classnames from 'classnames'
import { css } from 'emotion'
import * as _ from 'lodash'
import * as React from 'react'

import { formattedPhoneNumber } from 'embed/utils'
import { brandFromHost } from 'embed/constants/config'

import { ContactEven, ContactFiona, filterStyleCompact } from 'embed/constants'

import SortOptions, { SortStyles } from 'embed/components/SortOptions'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { DepositsOffer } from 'embed/constants/models'
import {
  Brand,
  CompactDepositsSortOptionValues,
  DepositsSortOption,
  DepositsSortOptionValues,
  SortDirection,
} from 'even-ts-static'
import AdvertiserDisclosure from '../AdvertiserDisclosure'
import DepositsOfferListItem from './list-item'

const styles = require('embed/styles/results')
const depositsOfferListStyles = require('./styles')

export interface Props {
  availableDepositsOffersTotal: number
  bestOfferId?: string
  customStyles?: SortStyles
  depositsOffers: DepositsOffer[]
  depositsOffersTotal: number
  dispatchClearFilters?: () => void
  dispatchOfferCta?: (offer: DepositsOffer) => void
  offerListRef: React.Ref<HTMLOListElement>
  onSortOptionClick: (sortOption: DepositsSortOption) => void
  sort: DepositsSortOption
  sortDirection: SortDirection
  useCompactListItem: boolean
  viewportHeight?: number
  viewportWidth?: number
}

const DepositsOfferList: React.SFC<Props> = ({
  availableDepositsOffersTotal,
  bestOfferId,
  customStyles,
  depositsOffers,
  depositsOffersTotal,
  dispatchClearFilters,
  dispatchOfferCta,
  offerListRef,
  onSortOptionClick,
  sort,
  sortDirection,
  useCompactListItem,
  viewportHeight,
  viewportWidth,
}) => {
  let errorMessage
  if (availableDepositsOffersTotal === 0) {
    const brand = brandFromHost
    // Error message if there are no offers available
    errorMessage = (
      <div data-test-id="depositsError__noOffers">
        <ThemeConsumer>
          {({ colorPrimary }) => {
            let emailAddress
            let phoneNumber
            // We don't care about the case where brand === Brand.LeapLife because we won't have leaplife-branded deposits offers
            if (brand === Brand.Even) {
              emailAddress = ContactEven.Email
              phoneNumber = ContactEven.Phone
            } else {
              emailAddress = ContactFiona.Email
              phoneNumber = ContactFiona.Phone
            }

            return (
              <div className={depositsOfferListStyles.emptyResults}>
                <p>
                  <strong>Sorry, looks like we haven&#8217;t been able to provide you with a range of offers.</strong>
                </p>
                <p>
                  We&#8217;d love for you to come back and try again soon.
                  <br />
                  Alternatively please contact us at{' '}
                  <a
                    href={`tel:${phoneNumber}`}
                    className={css`
                      color: ${colorPrimary};
                    `}
                  >
                    {formattedPhoneNumber(phoneNumber)}
                  </a>{' '}
                  or{' '}
                  <a
                    href={`mailto:${emailAddress}`}
                    className={css`
                      color: ${colorPrimary};
                    `}
                  >
                    {emailAddress}
                  </a>
                </p>
              </div>
            )
          }}
        </ThemeConsumer>
      </div>
    )
  } else if (depositsOffersTotal === 0) {
    // Error message if there are offers but they're being filtered out
    errorMessage = (
      <div data-test-id="depositsError__filteredOffers">
        <ThemeConsumer>
          {({ colorPrimary }) => (
            <div className={depositsOfferListStyles.emptyResults}>
              <p>
                <strong>No matching results.</strong>
              </p>
              <p>Enter different criteria to see more</p>
              <p>
                <a
                  className={css`
                    color: ${colorPrimary};
                  `}
                  onClick={dispatchClearFilters}
                >
                  Clear Filters
                </a>
              </p>
            </div>
          )}
        </ThemeConsumer>
      </div>
    )
  }

  return (
    <div>
      {depositsOffers.length > 0 && (
        <div className={styles.sortBarContainer}>
          <SortOptions
            customStyles={customStyles}
            onSortOptionClick={onSortOptionClick}
            sort={sort}
            sortDirection={sortDirection}
            sortOptionValues={useCompactListItem ? CompactDepositsSortOptionValues : DepositsSortOptionValues}
          />
          {!filterStyleCompact() && (
            <ThemeConsumer>
              {({ colorTextSecondary }) => (
                <div
                  className={classnames(
                    depositsOfferListStyles.depositsDisclosures,
                    css`
                      color: ${colorTextSecondary};
                    `,
                  )}
                >
                  Sponsored <span>|</span> <AdvertiserDisclosure className={styles.sortBarDisclosure} />
                </div>
              )}
            </ThemeConsumer>
          )}
        </div>
      )}
      <ol ref={offerListRef} className={styles.list}>
        {depositsOffers.map((offer) => (
          <DepositsOfferListItem
            bestOfferId={bestOfferId}
            dispatchOfferCta={dispatchOfferCta}
            key={offer.uuid}
            offer={offer}
            useCompactListItem={useCompactListItem}
            viewportHeight={viewportHeight}
            viewportWidth={viewportWidth}
          />
        ))}
      </ol>
      {errorMessage}
    </div>
  )
}

export default DepositsOfferList
